import "./App.css";
import Login from "./components/Login";
import Locations from "./components/Locations";
import {
  HashRouter as Router,
  Navigate,
  Route,
  Routes,
  useParams,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import AppointmentManagement from "./components/AppointmentManagement/AppointmentManagement";
import DoctorsList from "./components/DoctorsList/DoctorsList";
import AppointmentResearch from "./components/AppointmentResearch/AppointmentResearch";
import BookingCalendar from "./components/BookingCalendar";
import { CheckedServicesContextWrapper } from "./components/CheckedServices/Context/CheckedServicesContextWrapper";
import { HelmetProvider } from "react-helmet-async";
import MedintDetails from "./components/DoctorsList/Medint/MedintDetails/MedintDetails";
import ThemeWrapper from "./theme/ThemeWrapper";
import { MedintContextWrapper } from "./components/DoctorsList/Context/MedintContextWrapper";
import packageJson from "../package.json";
import RecurringBookingManagement from "./components/RecurringBookingManager/RecurringBookingManagement";
import { RecurringBookingContextWrapper } from "./components/RecurringBookingManager/Context/RecurringBookingContextWrapper";
import "dayjs/locale/it";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { itIT } from "@mui/x-date-pickers/locales";
import CheckedServicesMainPage from "./components/CheckedServices/CheckedServicesMainPage/CheckedServicesMainPage";
import { ContextWrapper } from "./components/AppointmentManagement/Calendar/Context/ContextWrapper";
import AuthGuard from "./hoc/authGuard/AuthGuard";
import UnAuthGuard from "./hoc/unAuthGuard/UnAuthGuard";
import { useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import { getToken, login, setToken } from "./api/gisApi";
import { AUTH_MODE } from "./api/client";
import { useAuthStore } from "./store/states/useAuthStore";

const queryClient = new QueryClient();

const MedintComponent = () => {
  const { id } = useParams<{ id: string }>();

  return (
    <MedintContextWrapper>
      {id ? <MedintDetails /> : <DoctorsList />}
    </MedintContextWrapper>
  );
};

const VersionLabel = (props: { version: string }) => {
  return (
    <div
      style={{
        position: "fixed",
        bottom: 15,
        right: 15,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        color: "white",
        padding: "5px 10px",
        borderRadius: "5px",
        fontSize: "12px",
        zIndex: 1000,
      }}
    >
      Versione {props.version}
    </div>
  );
};

function App() {
  // Bootstrap auth come in accettazioni: in produzione si esegue login()
  // (registerkc col token passato in querystring), poi si sincronizza lo
  // store Zustand useAuthStore — quello che AuthGuard controlla per decidere
  // se mostrare le rotte o reindirizzare al login.
  // In sviluppo si salta: la pagina di login gestisce l'autenticazione.
  const [isAuthenticated, setIsAuthenticated] = useState(
    AUTH_MODE === "development" ? true : false,
  );
  const { setAuth } = useAuthStore();

  useEffect(() => {
    if (AUTH_MODE === "development") return;

    async function initializeAuth() {
      console.log("[AUTH BOOT] initializeAuth: start (AUTH_MODE=production)");

      // Difesa preventiva: se l'URL ha già un token, lo mettiamo SUBITO in:
      // - Zustand store (così AuthGuard ha un valore utilizzabile)
      // - tokenCache via setToken (così getSid()/getToken() ritorna subito il
      //   token e le API che partono prima del completamento di login() non
      //   inviano sid="").
      try {
        const urlTokenEarly = new URLSearchParams(window.location.search).get("token");
        console.log(
          "[AUTH BOOT] urlToken letto dalla querystring:",
          urlTokenEarly ? `${urlTokenEarly.substring(0, 40)}... (${urlTokenEarly.length} chars)` : "NON PRESENTE",
        );
        if (urlTokenEarly) {
          setToken(urlTokenEarly); // popola tokenCache + localStorage + Zustand (via dynamic import)
          setAuth(urlTokenEarly);  // ridondante ma sicuro
          console.log("[AUTH BOOT] setToken+setAuth(urlToken) eseguito subito — cache + Zustand pronti");
        }
      } catch (e) {
        console.warn("[AUTH BOOT] lettura urlToken fallita:", e);
      }

      try {
        console.log("[AUTH BOOT] prima di await login()");
        await login();
        console.log("[AUTH BOOT] dopo await login() — login() ritornato senza eccezione");
        const token = getToken();
        console.log(
          "[AUTH BOOT] getToken() ritornato:",
          token ? `${token.substring(0, 40)}... (${token.length} chars)` : "VUOTO",
        );
        if (token) {
          setAuth(token); // sincronizza Zustand store per AuthGuard
          console.log("[AUTH BOOT] setAuth(token) eseguito");
        } else {
          console.warn("[AUTH BOOT] token VUOTO dopo login() — AuthGuard rediregerà al login");
        }
      } catch (error) {
        // errore registerkc: si mostra comunque l'app, AuthGuard gestirà il redirect
        console.error("[AUTH BOOT] eccezione in await login():", error);
      } finally {
        setIsAuthenticated(true);
        console.log("[AUTH BOOT] setIsAuthenticated(true) — bootstrap completato");
      }
    }
    initializeAuth();
  }, [setAuth]);

  if (!isAuthenticated) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      <QueryClientProvider client={queryClient}>
        <ThemeWrapper>
          <HelmetProvider>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale="it"
              localeText={
                itIT.components.MuiLocalizationProvider.defaultProps.localeText
              }
            >
              <Router>
                <Routes>
                  {/* Rotte non autenticate */}
                  <Route element={<UnAuthGuard />}>
                    <Route path="/login" element={<Login />} />
                  </Route>

                  {/* Rotte protette */}
                  <Route element={<AuthGuard />}>
                    {/* Root path: in iframe production il portale carica "/" e si
                        aspetta la landing dell'app prenotazioni → redirect a /gestione.
                        Prima qui c'era <Login /> dentro AuthGuard, che causava il
                        rendering della pagina di login anche ad autenticazione avvenuta. */}
                    <Route path="/" element={<Navigate to="/gestione" replace />} />
                    <Route path="/sedi" element={<Locations />} />
                    <Route
                      path="/gestione"
                      element={
                        <ContextWrapper>
                          <AppointmentManagement />
                        </ContextWrapper>
                      }
                    />
                    <Route path="/listinoMedici" element={<MedintComponent />} />
                    <Route path="/medint/:id" element={<MedintComponent />} />
                    <Route path="/medint" element={<MedintDetails />} />
                    <Route path="/research" element={<AppointmentResearch />} />
                    <Route path="/booking" element={<BookingCalendar />} />
                    <Route
                      path="/services"
                      element={
                        <CheckedServicesContextWrapper>
                          <CheckedServicesMainPage />
                        </CheckedServicesContextWrapper>
                      }
                    />
                    <Route
                      path="/prenotazioniCicliche"
                      element={
                        <RecurringBookingContextWrapper>
                          <RecurringBookingManagement />
                        </RecurringBookingContextWrapper>
                      }
                    />
                  </Route>

                  <Route path="*" element={<p>404 not found</p>} />
                </Routes>
              </Router>
            </LocalizationProvider>
          </HelmetProvider>
        </ThemeWrapper>
      </QueryClientProvider>
      <VersionLabel version={packageJson.version} />
    </>
  );
}

export default App;
